import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  $acceptInvitation,
  $declineInvitation,
  $getInvitationByToken,
} from "~/lib/invitations/functions";

const acceptSearchSchema = z.object({
  token: z.string(),
});

export const Route = createFileRoute("/invitations/accept")({
  validateSearch: acceptSearchSchema,
  loader: async ({ location }) => {
    const token = new URLSearchParams(location.search).get("token");
    if (!token) {
      return { data: null, error: "No invitation token provided" };
    }
    const result = await $getInvitationByToken({ data: { token } });
    return { data: result, token };
  },
  component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
  const { data, token } = Route.useLoaderData();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const acceptMutation = useMutation({
    mutationFn: () => $acceptInvitation({ data: { token: token! } }),
    onSuccess: async (result) => {
      // Remove any cached project data to ensure fresh fetch after membership is created
      // This is important because the project page may have cached an "unauthorized" error
      queryClient.removeQueries({ queryKey: ["projects", result.projectId] });
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate({ to: "/projects/$projectId", params: { projectId: result.projectId } });
    },
    onError: (error: Error) => {
      setAcceptError(error.message);
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => $declineInvitation({ data: { token: token! } }),
    onSuccess: () => {
      navigate({ to: "/dashboard" });
    },
    onError: (error: Error) => {
      setAcceptError(error.message);
    },
  });

  // Handle error states
  if (!data || "error" in data) {
    const errorMessage = data?.error ?? "Invalid invitation";
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button render={<Link to="/dashboard" />}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { invitation, isLoggedIn, emailMatches, currentUserEmail } = data;

  // Not logged in - show login prompt
  if (!isLoggedIn) {
    const returnUrl = `/invitations/accept?token=${token}`;
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Project Invitation</CardTitle>
            <CardDescription>
              You've been invited by <strong>{invitation.invitedByName}</strong> to join
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border p-4">
              <h3 className="font-semibold">{invitation.projectTitle}</h3>
              {invitation.projectDescription && (
                <p className="text-muted-foreground mt-1 text-sm">
                  {invitation.projectDescription}
                </p>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              This invitation was sent to <strong>{invitation.email}</strong>. Please log
              in with that email address to accept.
            </p>
            <div className="flex gap-2">
              <Button render={<Link to="/login" search={{ redirect: returnUrl }} />}>
                Log In
              </Button>
              <Button
                variant="outline"
                render={
                  <Link to="/signup" search={{ redirect: returnUrl, email: invitation.email }} />
                }
              >
                Sign Up
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in but email doesn't match
  if (!emailMatches) {
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Wrong Account</CardTitle>
            <CardDescription>
              This invitation was sent to a different email address.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
              <p className="text-sm">
                <strong>Invitation sent to:</strong> {invitation.email}
              </p>
              <p className="text-sm">
                <strong>You are logged in as:</strong> {currentUserEmail}
              </p>
            </div>
            <p className="text-muted-foreground text-sm">
              Please log out and sign in with <strong>{invitation.email}</strong> to
              accept this invitation.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" render={<Link to="/dashboard" />}>
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in and email matches - show accept button
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Project Invitation</CardTitle>
          <CardDescription>
            <strong>{invitation.invitedByName}</strong> has invited you to join their
            project as a {invitation.role}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border p-4">
            <h3 className="font-semibold">{invitation.projectTitle}</h3>
            {invitation.projectDescription && (
              <p className="text-muted-foreground mt-1 text-sm">
                {invitation.projectDescription}
              </p>
            )}
          </div>

          {acceptError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950">
              <p className="text-sm text-red-600 dark:text-red-400">{acceptError}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending || declineMutation.isPending}
            >
              {acceptMutation.isPending ? "Accepting..." : "Accept Invitation"}
            </Button>
            <Button
              variant="outline"
              onClick={() => declineMutation.mutate()}
              disabled={acceptMutation.isPending || declineMutation.isPending}
            >
              {declineMutation.isPending ? "Declining..." : "Decline"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
